/**
 * useScreener Hook
 *
 * List pending/approved/rejected senders, approve, reject, update via React Query.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api, EmailScreenedSender, EmailScreenedSenderWithMessage } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export function useScreener(accountId: string | undefined) {
  const queryClient = useQueryClient();

  const pendingQuery = useQuery({
    queryKey: queryKeys.email.screener(accountId ?? '', 'pending'),
    queryFn: async () => {
      if (!accountId) throw new Error('No account');
      return api.listPendingScreenedSenders(accountId);
    },
    enabled: !!accountId,
  });

  const approvedQuery = useQuery({
    queryKey: queryKeys.email.screener(accountId ?? '', 'approved'),
    queryFn: async () => {
      if (!accountId) throw new Error('No account');
      return api.listApprovedScreenedSenders(accountId);
    },
    enabled: !!accountId,
  });

  const rejectedQuery = useQuery({
    queryKey: queryKeys.email.screener(accountId ?? '', 'rejected'),
    queryFn: async () => {
      if (!accountId) throw new Error('No account');
      return api.listRejectedScreenedSenders(accountId);
    },
    enabled: !!accountId,
  });

  /**
   * Narrow invalidation: screener actions (approve/reject) reshuffle
   * screener buckets, contacts (an approved sender becomes a contact),
   * and messages+threads (a previously-quarantined sender's mail
   * appears in the inbox). Templates/signatures/snippets/snoozed are
   * untouched.
   */
  const invalidateAll = useCallback(() => {
    if (!accountId) return;
    queryClient.invalidateQueries({
      queryKey: queryKeys.email.all,
      predicate: (q) =>
        q.queryKey[0] === 'email' &&
        (q.queryKey[1] === 'screener' ||
          q.queryKey[1] === 'contacts' ||
          q.queryKey[1] === 'messages' ||
          q.queryKey[1] === 'threads'),
    });
  }, [accountId, queryClient]);

  const approve = useCallback(
    async (senderId: string) => {
      if (!accountId) return;
      const result = await api.approveScreenedSender(senderId, accountId);
      invalidateAll();
      return result;
    },
    [accountId, invalidateAll],
  );

  const reject = useCallback(
    async (senderId: string) => {
      if (!accountId) return;
      const result = await api.rejectScreenedSender(senderId, accountId);
      invalidateAll();
      return result;
    },
    [accountId, invalidateAll],
  );

  const updateStatus = useCallback(
    async (senderId: string, status: 'pending' | 'approved' | 'rejected') => {
      if (!accountId) return;
      const result = await api.updateScreenedSender(senderId, accountId, { status });
      invalidateAll();
      return result;
    },
    [accountId, invalidateAll],
  );

  return {
    pending: pendingQuery.data?.senders ?? ([] as EmailScreenedSenderWithMessage[]),
    approved: approvedQuery.data?.senders ?? ([] as EmailScreenedSender[]),
    rejected: rejectedQuery.data?.senders ?? ([] as EmailScreenedSender[]),
    pendingCount: pendingQuery.data?.senders?.length ?? 0,
    loading: pendingQuery.isLoading,
    approve,
    reject,
    updateStatus,
    refresh: () => invalidateAll(),
  };
}
